import { useTranslation } from 'react-i18next'
import { useField, useFormikContext } from 'formik'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import {
  Grid,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { BonusTriggerType } from '@/enums/be-bonus-trigger-type.enum'
import { ManualWalletAssign } from '@/enums/manual-assign-type'
import InputFormikField from '@/views/components/input-formik-field/index.component'

import { FormValuesManualWallet } from '@/views/wallets/hooks/use-wallets-schema.hook'
import { useEffect, useRef } from 'react'
import { getPlayersThunk } from '@/state/player-card/thunks/get-players.thunk'

export const PlayerUsernameComponent = () => {
  const { t } = useTranslation()

  const [fieldByPlayer] = useField('byPlayer')

  const location = useLocation()

  const isPlayerCard = location.pathname.includes('/player-card')

  const { setFieldValue, values } = useFormikContext<FormValuesManualWallet>()

  const dispatch = useDispatch<any>()
  const lastSearchedUsername = useRef('')
  const lastSearchedPlayerId = useRef('')

  useEffect(() => {
    if (values.bonusTrigger === BonusTriggerType.MANUAL) {
      setFieldValue('byPlayer', ManualWalletAssign.PLAYER)
    }
  }, [setFieldValue, values.bonusTrigger])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const performSearch = async () => {
    if (isPlayerCard) return

    if (
      values.byPlayer === ManualWalletAssign.USERNAME &&
      values.username &&
      values.username !== lastSearchedUsername.current
    ) {
      lastSearchedUsername.current = values.username
      setFieldValue('playerId', '')
      try {
        const res = await dispatch(
          getPlayersThunk({ username: values.username, limit: 25, page: 1 }),
        ).unwrap()

        if (res?.data && res.data.length > 0) {
          const player = res.data[0]
          if (player.id !== values.playerId) {
            setFieldValue('playerId', player.id)
          }
          if (player.username !== values.username) {
            setFieldValue('username', player.username)
            lastSearchedUsername.current = player.username
          }
        }
      } catch (err) {}
    } else if (
      values.byPlayer === ManualWalletAssign.PLAYER &&
      values.playerId &&
      values.playerId !== lastSearchedPlayerId.current
    ) {
      lastSearchedPlayerId.current = values.playerId
      setFieldValue('username', '')
      try {
        const res = await dispatch(
          getPlayersThunk({ playerId: values.playerId, limit: 25, page: 1 }),
        ).unwrap()

        if (res?.data && res.data.length > 0) {
          const player = res.data[0]
          if (player.username !== values.username) {
            setFieldValue('username', player.username)
          }
        }
      } catch (err) {}
    }
  }

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      performSearch()
    }, 2000)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [values.username, values.playerId, values.byPlayer, dispatch])

  const handleBlur = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    performSearch()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      performSearch()
    }
  }

  return (
    <>
      {!isPlayerCard && (
        <>
          <Grid item xs={12}>
            <Box mt={1}>
              <FormControl margin="dense">
                <RadioGroup
                  row
                  name="byPlayer"
                  value={fieldByPlayer.value}
                  onChange={(event) =>
                    setFieldValue('byPlayer', event.target.value)
                  }
                >
                  <FormControlLabel
                    value={ManualWalletAssign.USERNAME}
                    control={<Radio color="primary" />}
                    label={t('labels.username')}
                    labelPlacement="end"
                    sx={{
                      display:
                        values.bonusTrigger === BonusTriggerType.MANUAL
                          ? 'none'
                          : 'inherit',
                    }}
                  />
                  <FormControlLabel
                    value={ManualWalletAssign.PLAYER}
                    control={<Radio color="primary" />}
                    label={t('labels.playerId')}
                    labelPlacement="end"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Grid>
          {fieldByPlayer.value === ManualWalletAssign.PLAYER ? (
            <Grid item xs={12}>
              <InputFormikField
                label={t('labels.playerId')}
                name="playerId"
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <InputFormikField
                label={t('labels.username')}
                name="username"
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
              />
            </Grid>
          )}
        </>
      )}
    </>
  )
}
